import {joiResolver} from "@hookform/resolvers/joi";
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {GoogleMap, MarkerF, useLoadScript} from "@react-google-maps/api";
import {getGeocode} from "use-places-autocomplete";

import PlacesAutocomplete from "./PlacesAutocomplete";
import {citiesService} from "../../../services/cities.service";
import {stepperInfoActions} from "../../../store/slices/stepperInfoSlice";
import {parseGooglePlace} from "../../../utils/parseGooglePlace";
import {checkServiceability} from "../../../utils/serviceability";
import {locationPageValidator} from "../../../validators/locationPage.validator";
import styles from './LocationPage.module.scss';

// Constant references so useLoadScript / GoogleMap do not reload on every render.
const MAPS_LIBRARIES = ['places'];
const DEFAULT_CENTER = {lat: 49.8397, lng: 24.0297}; // Львів
const MAP_CONTAINER_STYLE = {width: '100%', height: '100%'};

const LocationPage = ({setCurrentStep}) => {
    const dispatch = useDispatch();

    const {
        selectedCity,
        address,
        street,
        buildingNumber,
        apartment,
        entrance,
        floor,
        intercomCode,
        postalCode,
        isPrivateHouse,
        latitude,
        longitude,
        comment,
        serviceabilityError,
    } = useSelector(state => state.stepperInfo);

    const [activeCities, setActiveCities] = useState([]);

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

    // Force Ukrainian place names + UA biasing so resolved cities/streets come
    // back in Ukrainian (e.g. "Львів", not "L'viv") and match the DB catalog.
    const {isLoaded, loadError} = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries: MAPS_LIBRARIES,
        language: 'uk',
        region: 'UA',
    });

    const {
        register,
        formState: {errors},
    } = useForm({
        resolver: joiResolver(locationPageValidator),
        mode: 'all',
    });

    const currentStep = JSON.parse(localStorage.getItem('currentStep'));

    useEffect(() => {
        // This page IS step 1. If the persisted step disagrees, normalize it in
        // place — do NOT navigate. navigate(CREATE_ORDER) bounces straight back
        // here via the index redirect, remounting this page → an infinite
        // ping-pong loop that storms GET /api/cities/ on every mount until the
        // browser runs out of sockets (net::ERR_INSUFFICIENT_RESOURCES).
        if (currentStep !== 1) {
            localStorage.setItem('currentStep', JSON.stringify(1));
            setCurrentStep?.(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo({top: 0, behavior: "smooth"});
    }, []);

    // Fetch the public list of active (serviceable) cities.
    useEffect(() => {
        let active = true;

        const fetchCities = async () => {
            try {
                const {data} = await citiesService.getActive();
                const list = Array.isArray(data) ? data : (data?.results || []);
                if (active) setActiveCities(list);
            } catch (error) {
                console.error('Failed to load serviceable cities:', error);
                if (active) setActiveCities([]);
            }
        };

        fetchCities();

        return () => {
            active = false;
        };
    }, []);

    // Re-evaluate serviceability whenever the resolved city or coords change.
    useEffect(() => {
        const {ok, error} = checkServiceability(selectedCity, latitude, longitude, activeCities);
        dispatch(stepperInfoActions.setIsServiceable(ok));
        dispatch(stepperInfoActions.setServiceabilityError(ok ? '' : error));
    }, [selectedCity, latitude, longitude, activeCities, dispatch]);

    const mapCenter = useMemo(() => {
        if (latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined) {
            return {lat: Number(latitude), lng: Number(longitude)};
        }
        return DEFAULT_CENTER;
    }, [latitude, longitude]);

    // Apply a parsed Google result + coords to the structured redux fields.
    const applyPlace = useCallback((result, lat, lng, placeId) => {
        const parsed = parseGooglePlace(result);

        if (parsed.address) dispatch(stepperInfoActions.setAddress(parsed.address));
        if (parsed.city) dispatch(stepperInfoActions.setSelectedCity(parsed.city));
        if (parsed.street) dispatch(stepperInfoActions.setStreet(parsed.street));
        if (parsed.buildingNumber) dispatch(stepperInfoActions.setBuildingNumber(parsed.buildingNumber));
        if (parsed.postalCode) dispatch(stepperInfoActions.setPostalCode(parsed.postalCode));

        if (lat !== null && lat !== undefined) dispatch(stepperInfoActions.setLatitude(lat));
        if (lng !== null && lng !== undefined) dispatch(stepperInfoActions.setLongitude(lng));
        if (placeId) dispatch(stepperInfoActions.setPlaceId(placeId));
    }, [dispatch]);

    const handleAutocompleteResolve = useCallback(({result, placeId, lat, lng}) => {
        applyPlace(result, lat, lng, placeId);
    }, [applyPlace]);

    // Приватний будинок: private houses have no flat-specific fields, so hide
    // and clear them when toggled on.
    const handleTogglePrivateHouse = useCallback((checked) => {
        dispatch(stepperInfoActions.setIsPrivateHouse(checked));

        if (checked) {
            dispatch(stepperInfoActions.setApartment(''));
            dispatch(stepperInfoActions.setEntrance(''));
            dispatch(stepperInfoActions.setFloor(''));
            dispatch(stepperInfoActions.setIntercomCode(''));
        }
    }, [dispatch]);

    // Dragging the marker reverse-geocodes and refills the address fields.
    const handleMarkerDragEnd = useCallback(async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        dispatch(stepperInfoActions.setLatitude(lat));
        dispatch(stepperInfoActions.setLongitude(lng));

        try {
            const results = await getGeocode({location: {lat, lng}});
            if (results && results[0]) {
                applyPlace(results[0], lat, lng, results[0].place_id);
            }
        } catch (error) {
            console.error('Reverse geocode error:', error);
        }
    }, [applyPlace, dispatch]);

    return (
        <div className={styles.locationPage_wrapper}>
            {isLoaded && !loadError && (
                <PlacesAutocomplete
                    initialValue={address || ''}
                    onResolve={handleAutocompleteResolve}
                />
            )}

            {(loadError || !apiKey) && (
                <p className={styles.mapsWarning}>
                    Пошук адреси тимчасово недоступний. Будь ласка, заповніть поля адреси вручну.
                </p>
            )}

            <form className={styles.form} action="#" autoComplete={'off'}>
                <label className={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={!!isPrivateHouse}
                        onChange={(e) => handleTogglePrivateHouse(e.target.checked)}
                        className={styles.checkbox}
                    />
                    <span>Приватний будинок</span>
                </label>

                <div className={styles.row}>
                    <label id={'city'} className={styles.label}>
                        <span>Місто <span className={styles.req}>*</span></span>
                        <select
                            {...register('city')}
                            value={selectedCity || ''}
                            onChange={(e) => dispatch(stepperInfoActions.setSelectedCity(e.target.value))}
                            className={styles.input}
                        >
                            <option value="" disabled>Оберіть місто</option>
                            {selectedCity &&
                                !activeCities.some(
                                    (c) => (c?.name || '').trim().toLowerCase() === selectedCity.trim().toLowerCase()
                                ) && (
                                    <option value={selectedCity}>{selectedCity}</option>
                                )}
                            {activeCities.map((c) => (
                                <option key={c.id ?? c.name} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                        {errors.city && <span className={styles.error}> {errors.city.message}</span>}
                    </label>

                    <label id={'street'} className={styles.label}>
                        <span>Вулиця <span className={styles.req}>*</span></span>
                        <input
                            {...register('street')}
                            value={street || ''}
                            onChange={(e) => dispatch(stepperInfoActions.setStreet(e.target.value))}
                            className={styles.input}
                            type="text"
                            placeholder={'Вулиця'}
                        />
                        {errors.street && <span className={styles.error}> {errors.street.message}</span>}
                    </label>
                </div>

                {/* Building row. For a private house the flat fields are hidden,
                    so the postal index moves up here to keep the grid balanced. */}
                <div className={styles.row}>
                    <label id={'buildingNumber'} className={styles.label}>
                        <span>Будинок <span className={styles.req}>*</span></span>
                        <input
                            {...register('buildingNumber')}
                            value={buildingNumber || ''}
                            onChange={(e) => dispatch(stepperInfoActions.setBuildingNumber(e.target.value))}
                            className={styles.input}
                            type="text"
                            placeholder={'Будинок'}
                        />
                        {errors.buildingNumber &&
                            <span className={styles.error}> {errors.buildingNumber.message}</span>}
                    </label>

                    {!isPrivateHouse && (
                        <>
                            <label className={styles.label}>
                                <span>Квартира</span>
                                <input
                                    value={apartment || ''}
                                    onChange={(e) => dispatch(stepperInfoActions.setApartment(e.target.value))}
                                    className={styles.input}
                                    type="text"
                                    placeholder={'Квартира'}
                                />
                            </label>

                            <label className={styles.label}>
                                <span>Підʼїзд</span>
                                <input
                                    value={entrance || ''}
                                    onChange={(e) => dispatch(stepperInfoActions.setEntrance(e.target.value))}
                                    className={styles.input}
                                    type="text"
                                    placeholder={'Підʼїзд'}
                                />
                            </label>
                        </>
                    )}

                    {isPrivateHouse && (
                        <label className={styles.label}>
                            <span>Поштовий індекс</span>
                            <input
                                value={postalCode || ''}
                                onChange={(e) => dispatch(stepperInfoActions.setPostalCode(e.target.value))}
                                className={styles.input}
                                type="text"
                                placeholder={'Поштовий індекс'}
                            />
                        </label>
                    )}
                </div>

                {!isPrivateHouse && (
                    <div className={styles.row}>
                        <label className={styles.label}>
                            <span>Поверх</span>
                            <input
                                value={floor || ''}
                                onChange={(e) => dispatch(stepperInfoActions.setFloor(e.target.value))}
                                className={styles.input}
                                type="text"
                                placeholder={'Поверх'}
                            />
                        </label>

                        <label className={styles.label}>
                            <span>Код домофону</span>
                            <input
                                value={intercomCode || ''}
                                onChange={(e) => dispatch(stepperInfoActions.setIntercomCode(e.target.value))}
                                className={styles.input}
                                type="text"
                                placeholder={'Код домофону'}
                            />
                        </label>

                        <label className={styles.label}>
                            <span>Поштовий індекс</span>
                            <input
                                value={postalCode || ''}
                                onChange={(e) => dispatch(stepperInfoActions.setPostalCode(e.target.value))}
                                className={styles.input}
                                type="text"
                                placeholder={'Поштовий індекс'}
                            />
                        </label>
                    </div>
                )}

                <label className={styles.label}>
                    <span>Коментар для клінера</span>
                    <textarea
                        value={comment || ''}
                        onChange={(e) => dispatch(stepperInfoActions.setComment(e.target.value))}
                        className={styles.input}
                        rows="3"
                        placeholder={'Напр. домофон не працює, телефонуйте; залиште інформацію, щоб клінер міг потрапити до вас'}
                    />
                </label>
            </form>

            {serviceabilityError && (
                <p className={styles.serviceError}>{serviceabilityError}</p>
            )}

            {isLoaded && !loadError && (
                <>
                    <p className={styles.mapHint}>
                        Перетягніть маркер, щоб уточнити точне розташування.
                    </p>
                    <div className={styles.map}>
                        <GoogleMap
                            mapContainerStyle={MAP_CONTAINER_STYLE}
                            center={mapCenter}
                            zoom={15}
                            options={{
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: false,
                            }}
                        >
                            <MarkerF
                                position={mapCenter}
                                draggable={true}
                                onDragEnd={handleMarkerDragEnd}
                            />
                        </GoogleMap>
                    </div>
                </>
            )}
        </div>
    );
};

export default LocationPage;
